import { Breadcrumbs, Link, Typography } from "@mui/material";
import React, { useContext } from "react";
import {
  Link as RouterLink,
  useLocation,
  useSearchParams,
} from "react-router-dom";
import { NavBarDispatchContext } from "../../contexts/NavBarContext";
import { ProjectContext } from "../../contexts/ProjectContext";

interface NestedPageParams {
  [page: string]: {
    entity: string;
    link: string;
    getExtraText: () => string | undefined | null;
  };
}

export default function SettingsBreadcrumb({
  isProject = true,
  settingsPage,
}: {
  isProject?: boolean;
  settingsPage: string;
}) {
  const navBarDispatch = useContext(NavBarDispatchContext);
  const { id, name, attributeTypes } = useContext(ProjectContext) || {};
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const mode = searchParams.get("mode");
  const nestedPages: NestedPageParams = {
    "Custom Categories": {
      entity: "Category",
      link: `/settings?projectId=${id}&settings=custom-categories`,
      getExtraText: () => {
        const attributeTypeId = searchParams.get("attributeTypeId");
        if (!attributeTypeId || !attributeTypes) return undefined;
        const attributeType = attributeTypes.find((attributeType) => {
          return attributeType.id === attributeTypeId;
        });
        return attributeType?.name;
      },
    },
    "Manage Users": {
      entity: "User",
      link: `/settings?settings=user`,
      getExtraText: () => {
        return searchParams.get("userName");
      },
    },
    "Flowsheet Groups": {
      entity: "Flowsheet Group",
      link: `/settings?settings=flowsheet-groups`,
      getExtraText: () => {
        return searchParams.get("name");
      },
    },
    "Flowsheet Details": {
      entity: "Flowsheet Groups Details",
      link: `/settings?settings=flowsheet-group-details`,
      getExtraText: () => {
        return "";
      },
    },
    "Lab Groups": {
      entity: "Lab Group",
      link: `/settings?settings=lab-groups`,
      getExtraText: () => {
        return searchParams.get("name");
      },
    },
    "Lab Details": {
      entity: "Lab Groups Details",
      link: `/settings?settings=lab-group-details`,
      getExtraText: () => {
        return "";
      },
    },
    "Prom Groups": {
      entity: "Prom Group",
      link: `/settings?settings=prom-groups`,
      getExtraText: () => {
        return searchParams.get("name");
      },
    },
    "Prom Details": {
      entity: "Prom Groups Details",
      link: `/settings?settings=prom-group-details`,
      getExtraText: () => {
        return "";
      },
    },
  };

  function getSettingText() {
    if (Object.keys(nestedPages).includes(settingsPage) && mode) {
      const page = nestedPages[settingsPage];
      return (
        <Link
          underline="hover"
          color="inherit"
          component={RouterLink}
          to={page.link}
          data-testid="settings-breadcrumb"
        >
          {settingsPage}
        </Link>
      );
    } else {
      return (
        <Typography data-testid="settings-breadcrumb">
          {settingsPage}
        </Typography>
      );
    }
  }

  function getExtraText() {
    if (Object.keys(nestedPages).includes(settingsPage) && mode) {
      const page = nestedPages[settingsPage];
      switch (mode) {
        case "create":
          return <Typography>New {page.entity}</Typography>;
        case "modify":
        case "detail": {
          const extraText = page.getExtraText();
          return <Typography>{extraText || "Loading"}</Typography>;
        }
        default:
          return undefined;
      }
    } else {
      return undefined;
    }
  }

  return isProject ? (
    <Breadcrumbs sx={{ marginBottom: "0.5rem" }}>
      <Link
        underline="hover"
        color="inherit"
        component={RouterLink}
        to={location.state?.previousPath || `/details?projectId=${id}`}
      >
        {name}
      </Link>
      <Link
        underline="hover"
        color="inherit"
        style={{ cursor: "pointer" }}
        onClick={() => navBarDispatch({ type: "openProject" })}
      >
        Project Settings
      </Link>
      {getSettingText()}
      {getExtraText()}
    </Breadcrumbs>
  ) : (
    <Breadcrumbs>
      <Link
        underline="hover"
        color="inherit"
        style={{ cursor: "pointer" }}
        onClick={() => navBarDispatch({ type: "openGlobal" })}
        data-testid="global-breadcrumb"
      >
        Global Settings
      </Link>
      {getSettingText()}
      {getExtraText()}
    </Breadcrumbs>
  );
}
